import { createFileRoute } from "@tanstack/react-router";
import { ServiceDetail } from "@/components/ServiceDetail";
import { serviceFaqs } from "@/data/serviceFaqs";
import { serviceGraph, abs } from "@/lib/serviceSchema";
import img from "@/assets/whole-home-audio.webp";

const bullets = [
  "Premium in-ceiling, in-wall, and bookshelf speaker options",
  "Authorized dealer of leading audio brands",
  "Independent or grouped zones — kitchen, patio, primary suite",
  "Native streaming from every major music service",
  "Architect-friendly, paintable grilles that disappear",
  "Subwoofer integration for full-range music in main spaces",
];

export const Route = createFileRoute("/services/whole-home-audio")({
  head: () => ({
    meta: [
      { title: "Whole-Home Audio & Multi-Room Speakers | Apex AV Denver" },
      { name: "description", content: "Multi-room audio installation across Denver. Premium in-wall and in-ceiling speakers — stream your favorite music services to any zone." },
      { name: "keywords", content: "whole-home audio Denver, multi-room audio Denver, in-ceiling speaker installation Denver, in-wall speaker installation Denver, home audio installation Denver, distributed audio Denver, outdoor speaker installation Denver, Sonos installer Denver, architectural speaker installation Denver, music system installation Denver, hidden speaker installation Denver" },
      { property: "og:title", content: "Whole-Home Audio — Apex AV Denver" },
      { property: "og:description", content: "Multi-room music streaming with hidden in-ceiling and in-wall speakers." },
      { property: "og:image", content: abs(img) },
      { name: "twitter:image", content: abs(img) },
      { property: "og:url", content: "https://apexav.com/services/whole-home-audio" },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: "Whole-Home Audio — Apex AV Denver" },
      { name: "twitter:description", content: "Multi-room music streaming with hidden in-ceiling and in-wall speakers." },
    ],
    links: [{ rel: "canonical", href: "https://apexav.com/services/whole-home-audio" }, { rel: "preload", as: "image", href: img, fetchPriority: "high" } as never],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceGraph({
            slug: "whole-home-audio",
            name: "Whole-Home Audio Installation",
            description: "Multi-room audio with hidden in-wall and in-ceiling speakers in Denver.",
            pageTitle: "Whole-Home Audio & Multi-Room Speakers | Apex AV Denver",
            pageDescription: "Multi-room audio installation across Denver. Premium in-wall and in-ceiling speakers — stream your favorite music services to any zone.",
            image: img,
            faqs: serviceFaqs["whole-home-audio"],
            serviceType: "Whole-Home Audio Installation",
            breadcrumbName: "Whole-Home Audio Installation",
            offerCatalog: bullets,
          }),
        ),
      },
    ],
  }),
  component: () => (
    <ServiceDetail
      faqs={serviceFaqs["whole-home-audio"]}
      topics={["whole-home-audio", "av-installation", "smart-home", "outdoor", "prewire", "whole-home-video"]}
      eyebrow="Whole-home audio"
      title="Music in every room of your home."
      intro={<>Discreet <strong>in-ceiling speaker installation</strong> and <strong>in-wall speakers</strong> in every space — kitchen, primary suite, patio, gym. Stream your favorite music services with <strong>whole-home audio in Denver</strong>, independently by zone or throughout the house.</>}
      image={img}
      imageAlt="Whole-home multi-room audio installation in a Lone Tree Denver luxury residence"
      brands={["sonos", "klipsch", "episode", "marantz", "denon", "sanus"]}
      bullets={bullets}
      features={[
        { title: "Any source, any room", desc: "Cooking jazz in the kitchen while the kids stream in their bedrooms." },
        { title: "Designer aesthetic", desc: "Magnetic, paintable, frameless grilles that vanish into the ceiling." },
        { title: "Streaming-native", desc: "No extra apps. Use the music services you already love." },
      ]}
    />
  ),
});
